import { useTranslation } from "react-i18next";
import NewModal from "../../../shared/NewModal";
import { useState, useEffect } from "react";
import TextField from "../../../Form/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft, faPen, faPeopleGroup, faBuilding,
  faWandMagicSparkles, faCheck, faLinkedin, faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin as faLinkedinBrand } from "@fortawesome/free-brands-svg-icons";
import TextArea from "../../../Form/TextArea";
import CreateContentPillars from "./Subsections/CreateContentPillars";
import UserConfig from "@serie3/mymediaplanner/Modules/User/Resources/Endpoints";
import crudApi from "@serie3/common/api/crudApi";
import ProfileForm from "@serie3/common/Modules/Profile/Forms/ProfileForm";
import { useFormik } from "formik";
import { ProfileConfig } from "@serie3/common/Modules/Profile/Resources/Endpoints";
import GetContentPillarsDTOForm from "@serie3/mymediaplanner/Modules/SetupAssistant/Forms/GetContentPillarsDTOForm";
import GroupForm from "@serie3/common/Modules/Groups/Forms/GroupForm";
import InvitationForm from "@serie3/common/Modules/Invitations/Forms/InvitationForm";

const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "#9299a8", marginBottom: 16 }}>
    {children}
  </p>
);

const NextBtn = ({ onClick, children, color = "#038edc", shadow = "rgba(3,142,220,0.3)" }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: "8px 22px", borderRadius: 50, border: "none",
      background: `linear-gradient(135deg,${color},${color}cc)`,
      color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
      boxShadow: `0 4px 14px ${shadow}`,
    }}
  >
    {children}
  </button>
);

const SetupAssistant = ({
  show,
  setShow,
  slideIndex,
  setSlideIndex,
  isFirstLogin,
  setIsFirstLogin,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(t("Backend.Assistant.Titles.Start"));
  const [, setHistory] = useState([]);
  const [, setSystemProfile] = useState(null);
  const [contentPillars, setContentPillars] = useState([]);
  const [group, setGroup] = useState(null);

  const createProfileForm = ProfileForm.createProfileForm(null, null, null, null, null, true);
  const profileFormik = useFormik(createProfileForm);

  useEffect(() => {
    const getSystemProfile = async () => {
      const response = await crudApi.justGet(ProfileConfig.endpoints.systemProfile);
      if (response.ok) {
        setSystemProfile(response.data);
        profileFormik.setValues(ProfileForm.setEditData(response.data));
      }
    };
    getSystemProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentPillarsForm = GetContentPillarsDTOForm.createGetContentPillarsDTOForm(setContentPillars);
  const contentPillarsFormik = useFormik(contentPillarsForm);

  const BRANCH = { creator: 2, agency: 5, brand: 10 };

  const goTo = (nextIndex) => {
    setHistory((prev) => [...prev, slideIndex]);
    setSlideIndex(nextIndex);
  };

  const goBack = () => {
    setHistory((prev) => {
      const copy = [...prev];
      const last = copy.pop();
      setSlideIndex(last ?? 0);
      return copy;
    });
  };

  const createGroupForm = GroupForm.createGroupForm(null, null, null, setGroup);
  const groupFormik = useFormik(createGroupForm);

  const createInvitationForm = InvitationForm.createInvitationForm(null, null, group && group.id);
  const invitationFormik = useFormik(createInvitationForm);

  const SlideButtons = () => (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      {slideIndex === 0 && (
        <button
          type="button"
          onClick={() => setShow(false)}
          style={{
            padding: "8px 18px", borderRadius: 50, border: "1.5px solid #e2e5ea",
            background: "#fff", color: "#6b7280", fontSize: 14, fontWeight: 600, cursor: "pointer", marginRight: 8,
          }}
        >
          {t("Backend.Assistant.Buttons.NotDoing")}
        </button>
      )}
      {slideIndex > 0 && (
        <button
          type="button"
          onClick={() => goBack()}
          style={{
            width: 36, height: 36, borderRadius: 50, border: "1.5px solid #e2e5ea",
            background: "#fff", color: "#374151", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 13 }} />
        </button>
      )}

      {slideIndex === 0 && (
        <NextBtn onClick={(e) => { goTo(1); profileFormik.handleSubmit(e); setTitle(t("Backend.Assistant.Titles.PickProfession")); }}>
          {t("Backend.Assistant.Buttons.SelectType")} →
        </NextBtn>
      )}
      {slideIndex === 2 && (
        <NextBtn onClick={(e) => { goTo(3); contentPillarsFormik.handleSubmit(e); }}>
          {t("Backend.Assistant.Buttons.CreateContentPillars")} →
        </NextBtn>
      )}
      {slideIndex === 3 && (
        <>
          <NextBtn onClick={(e) => { setContentPillars([]); contentPillarsFormik.handleSubmit(e); }} color="#f1734f" shadow="rgba(241,115,79,0.3)">
            {t("Backend.Assistant.Buttons.RecreatePillars")}
          </NextBtn>
          <NextBtn onClick={() => goTo(4)} color="#51d28c" shadow="rgba(81,210,140,0.3)">
            {t("Backend.Assistant.Buttons.Finalize")} →
          </NextBtn>
        </>
      )}
      {slideIndex === 5 && (
        <NextBtn onClick={(e) => { goTo(6); contentPillarsFormik.handleSubmit(e); }}>
          {t("Backend.Assistant.Buttons.CreateContentPillars")} →
        </NextBtn>
      )}
      {slideIndex === 6 && (
        <NextBtn onClick={() => goTo(7)}>
          {t("Backend.Assistant.Buttons.CreateGroups")} →
        </NextBtn>
      )}
      {slideIndex === 7 && (
        <NextBtn onClick={(e) => {
          goTo(8);
          const today = new Date();
          today.setMonth(today.getMonth() + 3);
          invitationFormik.setFieldValue("invited_till", today.toISOString().split("T")[0]);
          invitationFormik.handleSubmit(e);
          groupFormik.handleSubmit(e);
        }}>
          {t("Backend.Assistant.Buttons.InviteFirstClient")} →
        </NextBtn>
      )}
      {slideIndex === 8 && (
        <NextBtn onClick={(e) => { goTo(9); invitationFormik.handleSubmit(e); }} color="#51d28c" shadow="rgba(81,210,140,0.3)">
          {t("Backend.Assistant.Buttons.Finalize")} →
        </NextBtn>
      )}
      {slideIndex === 10 && (
        <NextBtn onClick={(e) => { goTo(11); groupFormik.handleSubmit(e); }}>
          {t("Backend.Assistant.Buttons.InviteTeammember")} →
        </NextBtn>
      )}
      {slideIndex === 11 && (
        <NextBtn onClick={(e) => {
          goTo(12);
          const today = new Date();
          today.setMonth(today.getMonth() + 3);
          invitationFormik.setFieldValue("invited_till", today.toISOString().split("T")[0]);
          invitationFormik.handleSubmit(e);
        }} color="#51d28c" shadow="rgba(81,210,140,0.3)">
          {t("Backend.Assistant.Buttons.InviteTeammember")} →
        </NextBtn>
      )}
    </div>
  );

  const Finish = () => (
    <div style={{ textAlign: "center", padding: "32px 0" }}>
      <div style={{
        width: 64, height: 64, borderRadius: "50%", margin: "0 auto 20px",
        background: "rgba(81,210,140,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <FontAwesomeIcon icon={faCheck} style={{ color: "#51d28c", fontSize: 28 }} />
      </div>
      <h3 style={{ fontFamily: "Montserrat,sans-serif", fontWeight: 800, fontSize: 24, color: "#111827", marginBottom: 10 }}>
        {t("Backend.Assistant.Finish.Title")}
      </h3>
      <p style={{ color: "#6b7280", fontSize: 15, maxWidth: 420, margin: "0 auto 32px" }}>
        {t("Backend.Assistant.Finish.Description")}
      </p>
      <a
        href="/callbacks/linkedin"
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "12px 28px", borderRadius: 50, textDecoration: "none",
          background: "linear-gradient(135deg,#0077b5,#005e8e)",
          color: "#fff", fontWeight: 700, fontSize: 15,
          boxShadow: "0 4px 16px rgba(0,119,181,0.35)",
        }}
      >
        <FontAwesomeIcon icon={faLinkedinBrand} style={{ fontSize: 18 }} />
        {t("Backend.Assistant.Buttons.ConnectNow")}
      </a>
    </div>
  );

  return (
    <NewModal
      show={show}
      setShow={setShow}
      title={title}
      ExtraButton={SlideButtons}
      onShow={() => {
        if (isFirstLogin) {
          crudApi.edit(UserConfig.endpoints.assistant.setFirstLoginFalse, {}, true);
          setIsFirstLogin(false);
          invitationFormik.resetForm();
          groupFormik.resetForm();
          contentPillarsFormik.resetForm();
          setGroup(null);
        }
      }}
    >
      {/* Step 0 — Name */}
      {slideIndex === 0 && (
        <div style={{ maxWidth: 420 }}>
          <SectionLabel>{t("Backend.Assistant.Titles.Start")}</SectionLabel>
          <div style={{ marginBottom: 16 }}>
            <TextField label="Vorname" formik={profileFormik} valueName="vorname" />
          </div>
          <TextField label="Name" formik={profileFormik} valueName="name" />
        </div>
      )}

      {/* Step 1 — Pick profession */}
      {slideIndex === 1 && (
        <>
          <SectionLabel>{t("Backend.Assistant.Titles.PickProfession")}</SectionLabel>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[
              { label: t("Backend.Assistant.Choice.Creator"), icon: faPen, color: "#038edc", branch: BRANCH.creator, titleKey: "Backend.Assistant.Titles.Describeyourself" },
              { label: t("Backend.Assistant.Choice.Coach"), icon: faPeopleGroup, color: "#51d28c", branch: BRANCH.agency, titleKey: "Backend.Assistant.Titles.Describeyourself" },
              { label: t("Backend.Assistant.Choice.Company"), icon: faBuilding, color: "#564ab1", branch: BRANCH.brand, titleKey: "Backend.Assistant.Titles.CreateTeam" },
            ].map(({ label, icon, color, branch, titleKey }) => (
              <button
                key={label}
                type="button"
                onClick={() => { setTitle(t(titleKey)); goTo(branch); }}
                style={{
                  flex: "1 1 160px", padding: "24px 20px", borderRadius: 14, textAlign: "left",
                  border: "2px solid #e2e5ea", background: "#fff",
                  cursor: "pointer", transition: "border-color 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e5ea"; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12, marginBottom: 14,
                  background: `${color}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FontAwesomeIcon icon={icon} style={{ color, fontSize: 20 }} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{label}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Step 2 — Creator: describe yourself */}
      {slideIndex === 2 && (
        <div style={{ maxWidth: 500 }}>
          <SectionLabel>Tell us about yourself</SectionLabel>
          <div style={{ marginBottom: 16 }}>
            <TextField label="Job title" formik={contentPillarsFormik} valueName="title" />
          </div>
          <TextArea label="Job description" formik={contentPillarsFormik} valueName="description" />
        </div>
      )}

      {/* Step 3 — Creator: Content pillars */}
      {slideIndex === 3 && (
        <>
          <SectionLabel>Your suggested content pillars</SectionLabel>
          <CreateContentPillars contentPillars={contentPillars} />
        </>
      )}

      {/* Step 4 — Creator: Finish */}
      {slideIndex === 4 && <Finish />}

      {/* Step 5 — Agency: describe topics */}
      {slideIndex === 5 && (
        <div style={{ maxWidth: 500 }}>
          <SectionLabel>Your main topics</SectionLabel>
          <div style={{ marginBottom: 16 }}>
            <TextField label="What are your own main topics?" formik={contentPillarsFormik} valueName="title" />
          </div>
          <TextArea label="Give a more detailed description" formik={contentPillarsFormik} valueName="description" />
        </div>
      )}

      {/* Step 6 — Agency: Content pillars */}
      {slideIndex === 6 && (
        <>
          <SectionLabel>Your suggested content pillars</SectionLabel>
          <CreateContentPillars contentPillars={contentPillars} />
        </>
      )}

      {/* Step 7 — Agency: Create first group */}
      {slideIndex === 7 && (
        <div style={{ maxWidth: 420 }}>
          <SectionLabel>Create your first client group</SectionLabel>
          <TextField label="Group name" formik={groupFormik} valueName="name" />
        </div>
      )}

      {/* Step 8 — Agency: Invite first client */}
      {slideIndex === 8 && (
        <div style={{ maxWidth: 420 }}>
          <SectionLabel>
            Invite your first client {group ? `to "${group.name}"` : ""}
          </SectionLabel>
          {group ? (
            <TextField label="Email" formik={invitationFormik} valueName="user_mail" />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, color: "#9299a8" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
              Creating your group…
            </div>
          )}
        </div>
      )}

      {/* Step 9 — Agency: Finish */}
      {slideIndex === 9 && <Finish />}

      {/* Step 10 — Corporate: Create team group */}
      {slideIndex === 10 && (
        <div style={{ maxWidth: 420 }}>
          <SectionLabel>Create your team group</SectionLabel>
          <TextField label="Group name" formik={groupFormik} valueName="name" />
        </div>
      )}

      {/* Step 11 — Corporate: Invite teammate */}
      {slideIndex === 11 && (
        <div style={{ maxWidth: 420 }}>
          <SectionLabel>
            Invite a teammate {group ? `to "${group.name}"` : ""}
          </SectionLabel>
          {group ? (
            <TextField label="Email" formik={invitationFormik} valueName="user_mail" />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, color: "#9299a8" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
              Creating your group…
            </div>
          )}
        </div>
      )}

      {/* Step 12 — Corporate: Finish */}
      {slideIndex === 12 && <Finish />}
    </NewModal>
  );
};

export default SetupAssistant;
